import { z } from 'zod';
import { StatefulColumnDefinitionSchema } from '@erp/rpc';
import type {
  WorkbookColumnType,
  WorkbookSinkMapping,
} from '@erp/shared-types';

type StatefulColumn = z.infer<typeof StatefulColumnDefinitionSchema>;
type StatefulColumnType = StatefulColumn['type'];

/**
 * The Stateful column type a Worksheet column of each Declared type is stored
 * as. A Date and time stays its ISO 8601 text: Stateful tables have no instant
 * type, and a date column would drop the time.
 */
const STATEFUL_COLUMN_TYPES: Record<WorkbookColumnType, StatefulColumnType> = {
  text: 'string',
  decimal: 'numeric',
  date: 'date',
  datetime: 'string',
  boolean: 'boolean',
};

/** Postgres's identifier limit, which Stateful column names share. */
const MAX_COLUMN_NAME = 63;

/** A Stateful column name for a Worksheet column: lower snake case, starting with a letter or underscore. */
export function statefulColumnName(column: string): string {
  const name = column
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return (/^[a-z]/.test(name) ? name : `_${name}`).slice(0, MAX_COLUMN_NAME);
}

/** The agent's create_stateful_table input. */
export const createStatefulTableInputSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2000).optional(),
  sheet: z
    .string()
    .min(1)
    .optional()
    .describe(
      "The worksheet the table takes columns from: its id, or its name. Its result's rows are what a sink output writes into the table.",
    ),
  columns: z
    .array(
      z.object({
        column: z
          .string()
          .min(1)
          .optional()
          .describe(
            "A worksheet column the table column holds; its type comes from the column's declared type.",
          ),
        name: z
          .string()
          .trim()
          .min(1)
          .max(MAX_COLUMN_NAME)
          .optional()
          .describe(
            "The table column's name; by default the worksheet column's, in lower snake case.",
          ),
        type: StatefulColumnDefinitionSchema.shape.type
          .optional()
          .describe(
            'Required for a column no worksheet column fills, such as a decision.',
          ),
        description:
          StatefulColumnDefinitionSchema.shape.description.optional(),
      }),
    )
    .max(100)
    .optional()
    .describe("The table's columns; every column of the worksheet by default."),
  keyColumns: z
    .array(z.string().min(1))
    .min(1)
    .max(8)
    .describe(
      'The columns that identify a row, by table column name or by worksheet column.',
    ),
});
export type CreateStatefulTableInput = z.infer<
  typeof createStatefulTableInputSchema
>;

/** The worksheet a table takes columns from, with its declared columns. */
export interface StatefulTableSheet {
  name: string;
  columns: readonly { name: string; type: WorkbookColumnType }[];
}

/** What to create, and the mappings a sink output from the worksheet needs. */
export interface StatefulTablePlan {
  columns: StatefulColumn[];
  keyColumns: string[];
  mappings: WorkbookSinkMapping[];
}

type ColumnRequest = NonNullable<CreateStatefulTableInput['columns']>[number];
/** A table column, with the worksheet column it holds when one fills it. */
interface PlannedColumn {
  column: StatefulColumn;
  source?: string;
}

/** A column no worksheet column fills, such as a decision. */
function newColumn({ name, type, description }: ColumnRequest): PlannedColumn {
  if (!name || !type)
    throw new Error(
      'A column no worksheet column fills needs a name and a type.',
    );
  return {
    column: {
      name,
      type,
      description: description ?? `The ${name} of the row.`,
    },
  };
}

/** A column holding a worksheet column, typed from its declared type. */
function worksheetColumn(
  request: ColumnRequest & { column: string },
  sheet: StatefulTableSheet | null,
): PlannedColumn {
  if (!sheet)
    throw new Error(
      `Column '${request.column}' names a worksheet column: pass the worksheet as sheet.`,
    );
  const source = sheet.columns.find((field) => field.name === request.column);
  if (!source)
    throw new Error(
      `The ${sheet.name} worksheet has no column '${request.column}'. Its columns: ${sheet.columns.map((field) => field.name).join(', ')}.`,
    );
  return {
    column: {
      name: request.name ?? statefulColumnName(source.name),
      type: request.type ?? STATEFUL_COLUMN_TYPES[source.type],
      description:
        request.description ??
        `The ${source.name} column of the ${sheet.name} worksheet.`.slice(
          0,
          500,
        ),
    },
    source: source.name,
  };
}

/**
 * The columns, key and sink mappings of a new Stateful table, checked as the
 * server checks them so the agent hears every problem before anything is made.
 */
export function statefulTablePlan(
  input: CreateStatefulTableInput,
  sheet: StatefulTableSheet | null,
): StatefulTablePlan {
  const requests =
    input.columns ??
    (sheet?.columns ?? []).map((field) => ({ column: field.name }));
  if (!requests.length)
    throw new Error(
      sheet
        ? `The ${sheet.name} worksheet has no columns yet: pass the columns to create.`
        : 'Pass a worksheet as sheet, or the columns to create.',
    );
  const planned = requests.map((request) =>
    request.column === undefined
      ? newColumn(request)
      : worksheetColumn({ ...request, column: request.column }, sheet),
  );
  const seen = new Set<string>();
  for (const { column } of planned) {
    if (!/^[a-z_][a-z0-9_]*$/i.test(column.name))
      throw new Error(
        `Column name '${column.name}' must use only letters, digits and underscores, and not start with a digit.`,
      );
    const key = column.name.toLowerCase();
    if (seen.has(key))
      throw new Error(
        `Two columns would be named '${column.name}': name one of them.`,
      );
    seen.add(key);
  }
  const keyColumns = input.keyColumns.map((key) => {
    const match =
      planned.find(({ column }) => column.name === key) ??
      planned.find(({ source }) => source === key);
    if (!match)
      throw new Error(
        `Key column '${key}' is not one of the table's columns: ${planned.map(({ column }) => column.name).join(', ')}.`,
      );
    return match.column.name;
  });
  return {
    columns: planned.map(({ column }) => column),
    keyColumns: [...new Set(keyColumns)],
    mappings: planned.flatMap(({ column, source }): WorkbookSinkMapping[] =>
      source === undefined
        ? []
        : [
            {
              targetColumn: column.name,
              value: { kind: 'column', column: source },
            },
          ],
    ),
  };
}
